import React, { useState } from "react";
import { FiPlay } from "react-icons/fi";

/**
 * Privacy- and performance-friendly YouTube embed.
 *
 * Uses the "facade" pattern: it shows YouTube's lightweight thumbnail image
 * and only loads the heavy iframe player when the user clicks Play. This keeps
 * the page fast (no iframe/scripts on initial load) and avoids loading
 * tracking cookies until the visitor actually watches.
 *
 * @param {string} youtubeId - The 11-char YouTube video id
 * @param {string} title - Accessible label / overlay caption
 * @param {("maxresdefault"|"hqdefault"|"mqdefault")} thumb - thumbnail quality
 */
export default function YouTubeVideo({ youtubeId, title = "YouTube video", thumb = "hqdefault" }) {
  const [activated, setActivated] = useState(false);

  if (!youtubeId) return null;

  const imgUrl = `https://i.ytimg.com/vi/${youtubeId}/${thumb}.jpg`;

  return (
    <div className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden group">
      {activated ? (
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0`}
          title={title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          onClick={() => setActivated(true)}
          className="absolute inset-0 w-full h-full cursor-pointer"
          aria-label={`Play video: ${title}`}
        >
          {/* Thumbnail (with graceful fallback via onError) */}
          <img
            src={imgUrl}
            alt={title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => {
              // maxres isn't always available; fall back to hqdefault
              if (!e.target.src.includes("hqdefault")) {
                e.target.src = `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;
              }
            }}
          />
          {/* Darken for contrast */}
          <span className="absolute inset-0 bg-gradient-to-t from-maroon-900/50 to-transparent" />
          {/* Play button */}
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-16 h-16 rounded-full bg-red-600 text-white flex items-center justify-center shadow-premium-lg group-hover:scale-110 group-hover:bg-red-500 transition-all">
              <FiPlay className="ml-1" size={26} fill="currentColor" />
            </span>
          </span>
        </button>
      )}
    </div>
  );
}
